import { useState } from 'react';
import { Link } from 'react-router';
import type { PublicListingDto } from 'shared';
import { CoverImage } from '../../components/CoverImage.js';
import { formatPrice } from './ListingCard.js';

/**
 * The showcase card — large, with an in-card carousel over `photoUrls`
 * (featured photo first, then the gallery). Each card owns its own
 * carousel position; nothing here is shared with sibling cards.
 */
export function NewRentalsCard({ listing }: { listing: PublicListingDto }) {
  const [index, setIndex] = useState(0);
  const photos = listing.photoUrls;
  const hasMultiple = photos.length > 1;

  function show(next: number) {
    setIndex(((next % photos.length) + photos.length) % photos.length);
  }

  const specs = [`${listing.bedrooms} bd`, `${listing.bathrooms} ba`].filter(Boolean);

  return (
    <article className="showcase-card">
      <div className="showcase-card__media">
        <span className="showcase-card__badge">New</span>
        <CoverImage
          src={photos[index] ?? null}
          alt={listing.addressLine1 || 'Rental listing'}
          variant="hero"
        />
        {hasMultiple ? (
          <>
            <button
              type="button"
              className="showcase-card__nav showcase-card__nav--prev"
              aria-label="Previous photo"
              onClick={() => show(index - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="showcase-card__nav showcase-card__nav--next"
              aria-label="Next photo"
              onClick={() => show(index + 1)}
            >
              ›
            </button>
            <div className="showcase-card__dots">
              {photos.map((photo, i) => (
                <button
                  key={photo}
                  type="button"
                  className={
                    i === index
                      ? 'showcase-card__dot showcase-card__dot--active'
                      : 'showcase-card__dot'
                  }
                  aria-label={`Show photo ${String(i + 1)} of ${String(photos.length)}`}
                  onClick={() => show(i)}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      <div className="showcase-card__body">
        <p className="showcase-card__price">${formatPrice(listing.price)}/month</p>
        <p className="showcase-card__specs">
          {specs.join(' · ')}
          {listing.addressLine1 ? ` · ${listing.addressLine1}` : ''}
        </p>
        {listing.description ? (
          <p className="showcase-card__description">{listing.description}</p>
        ) : null}
        <Link to={`/listings/${listing.id}`} className="button showcase-card__cta">
          View details
        </Link>
      </div>
    </article>
  );
}
